import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Plug, Loader2, X } from "lucide-react";
import { WiredConexao, WiredConexaoPayload, wiredService } from "../../../services/wired";

interface FormValues {
  nome: string;
  room_id: string;
  chave_leitura: string;
  chave_escrita: string;
}

interface WiredConexaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingConexao?: WiredConexao | null;
  onSuccess?: () => void;
}

const emptyForm: FormValues = { nome: "", room_id: "", chave_leitura: "", chave_escrita: "" };

export function WiredConexaoModal({ isOpen, onClose, editingConexao = null, onSuccess }: WiredConexaoModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormValues>(emptyForm);
  const [ativo, setAtivo] = useState(true);
  const [errors, setErrors] = useState<Partial<FormValues>>({});

  const validationSchema = Yup.object({
    nome: Yup.string()
      .trim()
      .required("Nome é obrigatório")
      .max(100, "Nome deve ter no máximo 100 caracteres"),
    room_id: Yup.string()
      .required("Room ID é obrigatório")
      .matches(/^\d+$/, "Room ID deve conter apenas números"),
    chave_leitura: editingConexao
      ? Yup.string()
      : Yup.string().trim().required("Chave de leitura é obrigatória"),
    chave_escrita: Yup.string(),
  });

  useEffect(() => {
    if (editingConexao) {
      setFormData({
        nome: editingConexao.nome,
        room_id: String(editingConexao.room_id),
        chave_leitura: "",
        chave_escrita: "",
      });
      setAtivo(editingConexao.ativo);
    } else {
      setFormData(emptyForm);
      setAtivo(true);
    }
    setErrors({});
  }, [editingConexao, isOpen]);

  const validateForm = () => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: Partial<FormValues> = {};
      error.inner.forEach((err: any) => {
        validationErrors[err.path as keyof FormValues] = err.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleInputChange = (field: keyof FormValues, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const payload: WiredConexaoPayload = {
        nome: formData.nome.trim(),
        room_id: parseInt(formData.room_id, 10),
        ativo,
      };
      // Chaves só são enviadas quando preenchidas; vazio mantém a atual
      if (formData.chave_leitura.trim()) payload.chave_leitura = formData.chave_leitura.trim();
      if (formData.chave_escrita.trim()) payload.chave_escrita = formData.chave_escrita.trim();

      if (editingConexao) {
        await wiredService.atualizarConexao(editingConexao.id, payload);
        enqueueSnackbar("Conexão atualizada com sucesso!", { variant: "success" });
      } else {
        await wiredService.criarConexao(payload);
        enqueueSnackbar("Conexão criada com sucesso!", { variant: "success" });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar conexão", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(emptyForm);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const leituraPlaceholder = editingConexao
    ? editingConexao.chave_leitura_mascarada
    : "Chave de leitura do quarto";
  const escritaPlaceholder = editingConexao?.possui_chave_escrita
    ? editingConexao.chave_escrita_mascarada || "••••••••"
    : "Chave de escrita (opcional)";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Plug className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {editingConexao ? "Editar Conexão" : "Nova Conexão"}
            </CardTitle>
            <CardDescription>
              {editingConexao
                ? "Atualize os dados da conexão com o quarto do Habbo"
                : "Cadastre uma conexão com um quarto do Habbo"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={submitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => handleInputChange("nome", e.target.value)}
                placeholder="Ex: Quarto principal"
                className={errors.nome ? "border-red-300" : ""}
                disabled={submitting}
              />
              {errors.nome && <p className="text-sm text-red-600">{errors.nome}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="room_id">Room ID</Label>
              <Input
                id="room_id"
                inputMode="numeric"
                value={formData.room_id}
                onChange={(e) => handleInputChange("room_id", e.target.value.replace(/\D/g, ""))}
                placeholder="Ex: 12345678"
                className={errors.room_id ? "border-red-300" : ""}
                disabled={submitting}
              />
              {errors.room_id && <p className="text-sm text-red-600">{errors.room_id}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="chave_leitura">Chave de leitura</Label>
              <Input
                id="chave_leitura"
                type="password"
                autoComplete="new-password"
                value={formData.chave_leitura}
                onChange={(e) => handleInputChange("chave_leitura", e.target.value)}
                placeholder={leituraPlaceholder}
                className={errors.chave_leitura ? "border-red-300" : ""}
                disabled={submitting}
              />
              {errors.chave_leitura && <p className="text-sm text-red-600">{errors.chave_leitura}</p>}
              {editingConexao && (
                <p className="text-xs text-muted-foreground">Deixe em branco para manter a atual.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="chave_escrita">Chave de escrita</Label>
              <Input
                id="chave_escrita"
                type="password"
                autoComplete="new-password"
                value={formData.chave_escrita}
                onChange={(e) => handleInputChange("chave_escrita", e.target.value)}
                placeholder={escritaPlaceholder}
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                {editingConexao
                  ? "Deixe em branco para manter a atual."
                  : "Necessária para enviar atualizações ao jogo. Opcional."}
              </p>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <Label htmlFor="ativo">Ativo</Label>
                <p className="text-xs text-muted-foreground">Conexões inativas não são utilizadas na sincronização.</p>
              </div>
              <Switch id="ativo" checked={ativo} onCheckedChange={setAtivo} disabled={submitting} />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingConexao ? "Atualizando..." : "Criando..."}
                  </>
                ) : editingConexao ? (
                  "Atualizar"
                ) : (
                  "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
